import * as React from 'react';
import type { StepAction, StepControlsView } from './controls.ts';

// The steps bar drawn from a `StepControlsView`: two zones in a row, the
// steps at the left end and the context at the right (2026-10-14). The
// steps: the inner two step, the outer two go to the
// previous and the next mistake (the same chevron as the step beside them,
// with a small triangle badge on the inner side in the blunder's or the
// miss's tone) or to the game's ends when no mistake lies that way
// (`view.outer`); or, while an owner holds the
// controls (a puzzle), look back ‹ and back to the puzzle ↶ only. The right
// holds the contextual exit, a secondary button with an icon and a label
// (icon only on a narrow bar): "Back to the game" on a line of the
// reader's own, "Back to the puzzle" while looking back in one; before one
// of the reader's mistakes, Puzzle; and, from the end of the opening on, on
// the game or on a line, Main line, beside whichever of those is there;
// nothing otherwise. The status line
// beneath is for assistive tech only; `children` go under it (the material
// strip). Presentational: the data as props, the clicks reported by action.

export interface StepControlsProps {
  view: StepControlsView;
  /** A step or the exit was clicked: the review method to call. */
  onAction: (action: StepAction) => void;
  /** Whatever other plugins put at the foot, the material strip first. */
  children?: React.ReactNode;
}

const Icon = {
  start: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="M56 40v176M200 48v160L80 128z" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  back: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="M160 48 80 128l80 80" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  forward: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="m96 48 80 80-80 80" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  end: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="M200 40v176M56 48v160l120-80z" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // A mistake behind or ahead: the blunders' triangle with a caret on the outer side.
  // A small filled triangle, the mistake badge beside an outer button's chevron, on its inner side
  // (in the blunder's or the miss's tone, by the button's class).
  badge: (
    <svg viewBox="0 0 256 256" aria-hidden="true" className="step-badge">
      <path d="M128 32 240 224H16Z" fill="currentColor" />
      <path d="M128 104v56m0 28v4" fill="none" stroke="var(--color-bg)" strokeWidth="26" strokeLinecap="round" />
    </svg>
  ),
  // The puzzle piece, filled with big tabs (2026-10-14: the small-tabbed one
  // melted into a blob at button size), the same glyph as the puzzle banner.
  puzzleAction: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="M72 104V72h40a24 24 0 1 1 32 0h40v40a24 24 0 1 0 0 40v40h-40a24 24 0 1 1-32 0H72v-40a24 24 0 1 1 0-40z" fill="currentColor" />
    </svg>
  ),
  // The board's green arrow (the best move's), as the button plays what it shows.
  mainLine: (
    <svg viewBox="0 0 256 256" aria-hidden="true" className="step-arrow-best">
      <path d="M40 216 176 80" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" />
      <path d="M112 56h88v88" fill="none" stroke="currentColor" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // Phosphor arrow-u-up-left: back to the puzzle's position after looking back.
  puzzle: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="m80 56-48 48 48 48M32 104h108a52 52 0 0 1 0 104h-20" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // Phosphor arrow-bend-up-left: back up the line to where it left the game.
  return: (
    <svg viewBox="0 0 256 256" aria-hidden="true">
      <path d="M80 56 32 104l48 48M32 104h104a64 64 0 0 1 64 64v40" fill="none" stroke="currentColor" strokeWidth="24" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

/** The key that does the same as the exit, for its title. */
const EXIT_KEY: Record<StepAction, string> = { toStart: '↑', stepBackward: '←', stepForward: '→', toEnd: '↓', returnToGame: 'Esc', outerBack: '↑', outerForward: '↓', puzzle: 'P', mainLine: 'L' };

export const StepControls: React.FC<StepControlsProps> = ({ view, onAction, children }) => {
  const { ready, held, deviated, labels, zones, outer, mainLine } = view;
  const canBack = ready && view.canBack;
  const canForward = ready && view.canForward;
  const exit = zones.right;

  const step = (icon: React.ReactNode, label: string, key: string, enabled: boolean, action: StepAction, extra = '', badge = false) => (
    <button
      type="button"
      className={`btn btn-ghost btn-icon step${extra}`}
      title={`${label} (${key})`}
      aria-label={label}
      disabled={!enabled}
      onClick={() => onAction(action)}>
      {icon}
      {badge && Icon.badge}
    </button>
  );

  return (
    <section
      className={`panel review-step-controls${deviated ? ' review-step-controls-line' : ''}${held ? ' review-step-controls-held' : ''}`}
      aria-label={held ? 'Look back through the puzzle' : 'Move through the game'}>
      <div className="steps">
        <div className="steps-centre">
          {zones.centre === 'held' ? (
            <>
              {step(Icon.back, labels.back, '←', canBack, 'stepBackward')}
              {step(Icon.puzzle, labels.end, '↓', canForward, 'toEnd', ' step-puzzle')}
            </>
          ) : (
            <>
              {step(
                outer?.back.kind === 'mistake' ? Icon.back : Icon.start,
                labels.start,
                '↑',
                canBack,
                outer ? 'outerBack' : 'toStart',
                outer?.back.kind === 'mistake' ? ` step-mistake step-mistake-back step-mistake-${outer.back.mistake}` : '',
                outer?.back.kind === 'mistake',
              )}
              {step(Icon.back, labels.back, '←', canBack, 'stepBackward')}
              {step(Icon.forward, labels.forward, '→', canForward, 'stepForward')}
              {step(
                outer?.forward.kind === 'mistake' ? Icon.forward : Icon.end,
                labels.end,
                '↓',
                canForward,
                outer ? 'outerForward' : 'toEnd',
                outer?.forward.kind === 'mistake' ? ` step-mistake step-mistake-forward step-mistake-${outer.forward.mistake}` : '',
                outer?.forward.kind === 'mistake',
              )}
            </>
          )}
        </div>
        <div className="steps-right">
          {exit?.kind === 'mistake' && (
            <button
              type="button"
              className="btn btn-secondary step-action step-action-puzzle"
              title={`Set this position up as a puzzle: find the better move than ${exit.san} (${EXIT_KEY.puzzle})`}
              aria-label="Puzzle: set this position up to solve"
              onClick={() => onAction('puzzle')}>
              {Icon.puzzleAction}
              <span className="step-action-label">Puzzle</span>
            </button>
          )}
          {mainLine && (
            <button
              type="button"
              className="btn btn-secondary step-action step-action-line"
              title={`Play the best moves from here, the green arrow's, a few moves each side (${EXIT_KEY.mainLine})`}
              aria-label="Main line: play the best moves from here"
              onClick={() => onAction('mainLine')}>
              {Icon.mainLine}
              <span className="step-action-label">Main line</span>
            </button>
          )}
          {exit && exit.kind !== 'mistake' && (
            <button
              type="button"
              className={`btn btn-secondary step-exit step-exit-${exit.kind}`}
              title={`${exit.label} (${EXIT_KEY[exit.action]})`}
              aria-label={exit.label}
              onClick={() => onAction(exit.action)}>
              {exit.kind === 'game' ? Icon.return : Icon.puzzle}
              <span className="step-exit-label">{exit.label}</span>
            </button>
          )}
        </div>
      </div>
      {/* The status is read by assistive tech only; the sheet and board say where the reader is. */}
      <p className="step-status visually-hidden" aria-live="polite">
        {view.status}
      </p>
      {children}
    </section>
  );
};
